import { CommonActions } from '@react-navigation/native';
import type { NavigationState } from '@react-navigation/native';
import { buildAppRootResetAction } from '../../navigation/rootNavigationAuth';
import { isGuestWelcomeRoute, resolveNavigationIntent } from './audienceGuard';
import { MAIN_TAB_INITIAL_ROUTE } from './tabConstants';

/** Minimal navigation shape for service helpers. */
export type NavigationHandle = {
  canGoBack: () => boolean;
  goBack: () => void;
  navigate: (name: string, params?: object) => void;
  getState?: () => NavigationState;
  getParent?: () => NavigationHandle | undefined;
  dispatch?: (action: ReturnType<typeof CommonActions.reset>) => void;
};

function findRootNavigation(navigation: NavigationHandle): NavigationHandle {
  let current: NavigationHandle | undefined = navigation;
  while (current?.getParent?.()) {
    current = current.getParent();
  }
  return current ?? navigation;
}

export function resetAuthenticatedHome(navigation: NavigationHandle, tab: string = MAIN_TAB_INITIAL_ROUTE): void {
  const root_navigation = findRootNavigation(navigation);
  const reset_action = buildAppRootResetAction(true);
  if (root_navigation.dispatch) {
    root_navigation.dispatch(reset_action);
    return;
  }
  navigation.navigate('MainTabs', { screen: tab });
}

export function resetGuestHome(navigation: NavigationHandle): void {
  const root_navigation = findRootNavigation(navigation);
  const reset_action = buildAppRootResetAction(false);
  if (root_navigation.dispatch) {
    root_navigation.dispatch(reset_action);
    return;
  }
  navigation.navigate('Main');
}

export function navigationServiceNavigate(
  navigation: NavigationHandle,
  route_name: string,
  params: object | undefined,
  is_authenticated: boolean,
): void {
  const intent = resolveNavigationIntent(route_name, is_authenticated);
  if (intent.action === 'redirect') {
    if (intent.target === 'authenticated_home') {
      resetAuthenticatedHome(navigation);
    } else {
      resetGuestHome(navigation);
    }
    return;
  }
  if (intent.action === 'block') {
    return;
  }
  navigation.navigate(route_name, params);
}

export function getPreviousStackRouteName(state: NavigationState | undefined): string | undefined {
  if (!state?.routes?.length) {
    return undefined;
  }
  const index = state.index ?? 0;
  if (index <= 0) {
    return undefined;
  }
  return state.routes[index - 1]?.name;
}

export function exitOrGoBackViaService(navigation: NavigationHandle, is_authenticated: boolean): void {
  const previous_route = getPreviousStackRouteName(navigation.getState?.());

  if (is_authenticated && isGuestWelcomeRoute(previous_route)) {
    resetAuthenticatedHome(navigation);
    return;
  }

  if (navigation.canGoBack()) {
    navigation.goBack();
    return;
  }

  if (is_authenticated) {
    resetAuthenticatedHome(navigation);
  } else {
    navigation.navigate('Main');
  }
}
